// PURPOSE: Start and review linked polls from native client and outreach menus.
// CONTRACT: Spec 066; stable creation retry and clipboard success only after copying.
// DEPENDS: Native session, Query cache, Router and findtime API.
// CEILING: No invites, sends or browser permission authority.
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useSession } from './auth';
import type { NativeRecord } from './types';
import type { Poll } from './findtime-state';

export function useClientPoll(record: NativeRecord) {
  const { api, me } = useSession();
  const cache = useQueryClient();
  const operation = useMemo(() => ({ key: '', created: null as Poll | null, pending: false }), [record.id]);
  const [feedback, setFeedback] = useState({ clientId: '', message: '', error: '', link: '' });
  const visible = feedback.clientId === record.id ? feedback : { message: '', error: '', link: '' };
  const allowed = (!me.modules || me.modules.includes('schedule')) &&
    (me.role === 'Admin' || (me.role === 'AE' && record.data.assigned_ae_id === me.id));
  const start = async () => {
    if (operation.pending) return;
    operation.pending = true;
    const next = { clientId: record.id, message: '', error: '', link: '' };
    setFeedback(next);
    try {
      operation.key ||= crypto.randomUUID();
      const poll = operation.created || await api.request<Poll>(`/findtime/from-client/${record.id}`, {
        method: 'POST', body: JSON.stringify({ idempotency_key: operation.key }),
      });
      operation.created = poll;
      const url = `${location.origin}/f/${poll.token}`; next.link = url;
      await cache.invalidateQueries({ queryKey: ['findtime'] });
      await navigator.clipboard.writeText(url);
      next.message = `Link copied, send it to ${record.data.name}`;
      operation.created = null; operation.key = "";
    } catch (e) { next.error = operation.created ? 'Copy the link below.' : e instanceof Error ? e.message : String(e); }
    finally { operation.pending = false; setFeedback({ ...next }); }
  };
  return { allowed, start, ...visible };
}

export function PollFeedback({ poll }: { poll: ReturnType<typeof useClientPoll> }) {
  return <>{poll.message && <p role="status" className="success-message">{poll.message}</p>}{poll.error && <p role="alert">{poll.error}</p>}{poll.error && poll.link && <input aria-label="Poll link" readOnly value={poll.link} onFocus={e => e.currentTarget.select()} />}</>;
}

export function ClientPolls({ clientId, ownerId }: { clientId: string; ownerId?: string }) {
  const { api, me } = useSession();
  const allowed = (!me.modules || me.modules.includes('schedule')) && (me.role === 'Admin' || (me.role === 'AE' && ownerId === me.id));
  const polls = useQuery({ queryKey: ['findtime', 'client', me.tenant_id, me.id, clientId],
    enabled: allowed,
    queryFn: () => api.request<{ items: Poll[] }>(`/findtime?client_id=${encodeURIComponent(clientId)}`) });
  if (!allowed) return null;
  if (polls.error) return <p role="alert">Polls could not be loaded.</p>;
  if (!polls.data?.items?.length) return null;
  return <section className="panel" aria-label="Availability polls">{polls.data.items.map(poll => <p key={poll.id}><Link to={`/schedule/find-time?poll=${poll.id}`}>Poll {poll.status} · {poll.response_count || 0} responses</Link></p>)}</section>;
}
